import React, { useMemo, useState, useEffect } from "react";
import { Grid } from "@aws-amplify/ui-react";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ConfirmModal from "components/utils/ConfirmModal";

ChartJS.register(
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
);

const sampleData = {
  farms: [
    { id: "farm-1", name: "Finca La Esperanza", active: true },
    { id: "farm-2", name: "Finca El Roble", active: true },
  ],
  lots: [
    {
      id: "lot-1",
      farmId: "farm-1",
      name: "Lote 1",
      cropUnit: "Café",
      active: true,
    },
    {
      id: "lot-2",
      farmId: "farm-1",
      name: "Lote 2",
      cropUnit: "Banano",
      active: true,
    },
    {
      id: "lot-3",
      farmId: "farm-2",
      name: "Lote A",
      cropUnit: "Cacao",
      active: true,
    },
  ],
  // saleRecords accumulate ingresos
  saleRecords: [
    {
      id: "s1",
      date: "2025-10-01",
      farmId: "farm-1",
      lotId: "lot-1",
      totalSale: 1200000,
    },
    {
      id: "s2",
      date: "2025-10-03",
      farmId: "farm-1",
      lotId: "lot-2",
      totalSale: 2400000,
    },
    {
      id: "s3",
      date: "2025-10-05",
      farmId: "farm-2",
      lotId: "lot-3",
      totalSale: 600000,
    },
    {
      id: "s4",
      date: "2025-09-20",
      farmId: "farm-1",
      lotId: "lot-1",
      totalSale: 900000,
    },
    {
      id: "s5",
      date: "2025-09-28",
      farmId: "farm-2",
      lotId: "lot-3",
      totalSale: 1800000,
    },
  ],
  // expenseRecords classify costs (Insumos, Equipo, Herramientas, Gastos e inversiones)
  expenseRecords: [
    {
      id: "e1",
      date: "2025-10-02",
      farmId: "farm-1",
      lotId: "lot-1",
      type: "Insumos",
      description: "Fertilizante",
      value: 250000,
    },
    {
      id: "e2",
      date: "2025-10-04",
      farmId: "farm-1",
      lotId: "lot-2",
      type: "Equipo",
      description: "Mantenimiento tractor",
      value: 800000,
    },
    {
      id: "e3",
      date: "2025-09-29",
      farmId: "farm-2",
      lotId: "lot-3",
      type: "Herramientas",
      description: "Herramientas mano",
      value: 120000,
    },
    {
      id: "e4",
      date: "2025-10-07",
      farmId: "farm-1",
      lotId: "lot-1",
      type: "Gastos e Inversiones",
      description: "Reparación bodega",
      value: 900000,
    },
    {
      id: "e5",
      date: "2025-10-08",
      farmId: "farm-2",
      lotId: "lot-3",
      type: "Insumos",
      description: "Plaguicida",
      value: 150000,
    },
    {
      id: "e6",
      date: "2025-09-25",
      farmId: "farm-1",
      lotId: "lot-2",
      type: "Insumos",
      description: "Semillas",
      value: 200000,
    },
  ],
};

export default function ReportPYG(props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = props.data ||
    sampleData || { farms: [], lots: [], saleRecords: [], expenseRecords: [] };

  // filters: period range + farm
  const saleDates = useMemo(() => {
    const dates = [
      ...(data.saleRecords || []).map((s) => s.date),
      ...(data.expenseRecords || []).map((e) => e.date),
    ];
    dates.sort();
    return dates;
  }, [data]);

  const defaultStart = saleDates[0] || moment().format("YYYY-MM-DD");
  const defaultEnd =
    saleDates[saleDates.length - 1] || moment().format("YYYY-MM-DD");

  const farms = useMemo(
    () => (data.farms || []).filter((f) => f.active),
    [data]
  );
  const [selectedFarmId, setSelectedFarmId] = useState(farms[0]?.id || "");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  useEffect(() => setSelectedFarmId(farms[0]?.id || ""), [farms]);
  useEffect(() => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  }, [defaultStart, defaultEnd]);

  // lots for selected farm
  const lotsByFarm = useMemo(
    () =>
      (data.lots || []).filter((l) => l.farmId === selectedFarmId && l.active),
    [data, selectedFarmId]
  );

  // filtered sales and expenses by date range and farm
  const filteredSales = useMemo(() => {
    const s = moment(startDate, "YYYY-MM-DD");
    const e = moment(endDate, "YYYY-MM-DD").endOf("day");
    return (data.saleRecords || []).filter((r) => {
      const d = moment(r.date, "YYYY-MM-DD");
      if (!d.isBetween(s, e, null, "[]")) return false;
      if (selectedFarmId && r.farmId !== selectedFarmId) return false;
      return true;
    });
  }, [data, startDate, endDate, selectedFarmId]);

  const filteredExpenses = useMemo(() => {
    const s = moment(startDate, "YYYY-MM-DD");
    const e = moment(endDate, "YYYY-MM-DD").endOf("day");
    return (data.expenseRecords || []).filter((r) => {
      const d = moment(r.date, "YYYY-MM-DD");
      if (!d.isBetween(s, e, null, "[]")) return false;
      if (selectedFarmId && r.farmId !== selectedFarmId) return false;
      return true;
    });
  }, [data, startDate, endDate, selectedFarmId]);

  // aggregate per lot: income, cost, net
  const perLotAggregation = useMemo(() => {
    const map = {};
    (lotsByFarm || []).forEach((l) => {
      map[l.id] = { lotName: l.name, income: 0, cost: 0, net: 0 };
    });
    filteredSales.forEach((s) => {
      map[s.lotId] = map[s.lotId] || {
        lotName:
          (data.lots || []).find((ll) => ll.id === s.lotId)?.name || s.lotId,
        income: 0,
        cost: 0,
        net: 0,
      };
      map[s.lotId].income += Number(s.totalSale || 0);
    });
    filteredExpenses.forEach((e) => {
      map[e.lotId] = map[e.lotId] || {
        lotName:
          (data.lots || []).find((ll) => ll.id === e.lotId)?.name || e.lotId,
        income: 0,
        cost: 0,
        net: 0,
      };
      map[e.lotId].cost += Number(e.value || 0);
    });
    Object.keys(map).forEach((k) => {
      map[k].net = (map[k].income || 0) - (map[k].cost || 0);
    });
    return map;
  }, [lotsByFarm, filteredSales, filteredExpenses, data]);

  // totals
  const totalIncome = useMemo(
    () =>
      Object.values(perLotAggregation).reduce(
        (acc, v) => acc + (v.income || 0),
        0
      ),
    [perLotAggregation]
  );
  const totalCost = useMemo(
    () =>
      Object.values(perLotAggregation).reduce(
        (acc, v) => acc + (v.cost || 0),
        0
      ),
    [perLotAggregation]
  );
  const totalNet = useMemo(
    () => totalIncome - totalCost,
    [totalIncome, totalCost]
  );

  // Bar chart: incomes vs costs per lot (two datasets)
  const lotLabels = useMemo(
    () => Object.values(perLotAggregation).map((v) => v.lotName || v.lotId),
    [perLotAggregation]
  );
  const incomeValues = useMemo(
    () => Object.values(perLotAggregation).map((v) => v.income || 0),
    [perLotAggregation]
  );
  const costValues = useMemo(
    () => Object.values(perLotAggregation).map((v) => v.cost || 0),
    [perLotAggregation]
  );

  // Pie chart: cost breakdown by type
  const costByType = useMemo(() => {
    const map = {};
    (filteredExpenses || []).forEach((e) => {
      const t = e.type || "Otro";
      map[t] = (map[t] || 0) + Number(e.value || 0);
    });
    return map;
  }, [filteredExpenses]);

  const costTypeLabels = useMemo(() => Object.keys(costByType), [costByType]);
  const costTypeValues = useMemo(
    () => costTypeLabels.map((k) => costByType[k]),
    [costTypeLabels, costByType]
  );

  // pastel palette
  const PASTEL_COLORS = [
    "#90d47dff",
    "#f09bb4ff",
    "#fcdc86ff",
    "#8eb4ffff",
    "#ce99f7ff",
    "#ffbe90ff",
    "#98eccdff",
  ];

  const colorsForLotsIncome = useMemo(
    () => lotLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lotLabels]
  );
  const colorsForLotsCost = useMemo(
    () =>
      lotLabels.map((_, i) => PASTEL_COLORS[(i + 2) % PASTEL_COLORS.length]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lotLabels]
  );
  const colorsForCostTypes = useMemo(
    () => costTypeLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [costTypeLabels]
  );

  const chartHeightPx = props.chartHeightPx || 360;

  const barData = useMemo(
    () => ({
      labels: lotLabels,
      datasets: [
        {
          label: "Ingresos",
          data: incomeValues,
          backgroundColor: colorsForLotsIncome,
          borderColor: colorsForLotsIncome,
          borderWidth: 1,
        },
        {
          label: "Costos",
          data: costValues,
          backgroundColor: colorsForLotsCost,
          borderColor: colorsForLotsCost,
          borderWidth: 1,
        },
      ],
    }),
    [
      lotLabels,
      incomeValues,
      costValues,
      colorsForLotsIncome,
      colorsForLotsCost,
    ]
  );

  const pieData = useMemo(
    () => ({
      labels: costTypeLabels,
      datasets: [
        {
          data: costTypeValues,
          backgroundColor: colorsForCostTypes,
          hoverOffset: 8,
        },
      ],
    }),
    [costTypeLabels, costTypeValues, colorsForCostTypes]
  );

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  const barOptions = {
    ...commonOptions,
    plugins: { ...commonOptions.plugins, legend: { position: "top" } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const pieOptions = {
    ...commonOptions,
    plugins: { ...commonOptions.plugins },
  };

  // table of per-lot details
  const perLotRows = useMemo(
    () =>
      Object.entries(perLotAggregation).map(([lotId, v]) => ({
        id: lotId,
        lotName: v.lotName,
        income: v.income,
        cost: v.cost,
        net: v.net,
      })),
    [perLotAggregation]
  );

  // modal placeholders
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  function handleConfirmDelete() {
    setModalDetails({ visible: false, itemId: null });
  }

  return (
    <div>
      <span className="fs-3">PYG</span>

      <div className="p-3">
        {/* filtros mínimos: periodo (rango) + finca */}
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <div>
            <label className="form-label">Fecha desde</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label">Fecha hasta</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label">Finca</label>
            <select
              className="form-select"
              value={selectedFarmId}
              onChange={(e) => setSelectedFarmId(e.target.value)}
            >
              {(farms || []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        </Grid>

        {/* tarjetas mínimas */}
        <CRow className="mb-3">
          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">
                Total Ingresos (periodo)
              </CCardHeader>
              <CCardBody>
                <div className="fs-4">
                  {totalIncome.toLocaleString("es-CO")}
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">
                Total Costos (periodo)
              </CCardHeader>
              <CCardBody>
                <div className="fs-4">{totalCost.toLocaleString("es-CO")}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">PYG Neto</CCardHeader>
              <CCardBody>
                <div className="fs-4">{totalNet.toLocaleString("es-CO")}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* gráficos: Barras (Ingresos vs Costos por Lote) y Torta (Costos por Tipo) */}
        <CRow>
          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">
                Ingresos vs Costos por Lote
              </CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {lotLabels.length === 0 ? (
                  <div className="text-muted">
                    No hay lotes para la finca seleccionada.
                  </div>
                ) : (
                  <Bar data={barData} options={barOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">
                Costos por Tipo (periodo)
              </CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {costTypeLabels.length === 0 ? (
                  <div className="text-muted">
                    No hay costos en el periodo seleccionado.
                  </div>
                ) : (
                  <Pie data={pieData} options={pieOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* tabla con totales por lote y totalización final */}
        <CRow>
          <CCol>
            <CCard>
              <CCardHeader>
                Totales por Lote (Ingresos / Costos / Neto)
              </CCardHeader>
              <CCardBody>
                <div className="table-responsive">
                  <CTable hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>Lote</CTableHeaderCell>
                        <CTableHeaderCell>Ingresos</CTableHeaderCell>
                        <CTableHeaderCell>Costos</CTableHeaderCell>
                        <CTableHeaderCell>PYG Neto</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {perLotRows.length === 0 ? (
                        <CTableRow>
                          <CTableDataCell colSpan={4} className="text-muted">
                            No hay datos para los filtros seleccionados.
                          </CTableDataCell>
                        </CTableRow>
                      ) : (
                        perLotRows.map((r) => (
                          <CTableRow key={r.id}>
                            <CTableDataCell>{r.lotName ?? r.id}</CTableDataCell>
                            <CTableDataCell>
                              {(Number(r.income) || 0).toLocaleString("es-CO")}
                            </CTableDataCell>
                            <CTableDataCell>
                              {(Number(r.cost) || 0).toLocaleString("es-CO")}
                            </CTableDataCell>
                            <CTableDataCell>
                              {(Number(r.net) || 0).toLocaleString("es-CO")}
                            </CTableDataCell>
                          </CTableRow>
                        ))
                      )}
                    </CTableBody>
                  </CTable>
                </div>

                <div className="mt-3 text-end">
                  <strong>Totales (periodo):</strong> Ingresos{" "}
                  {totalIncome.toLocaleString("es-CO")} — Costos{" "}
                  {totalCost.toLocaleString("es-CO")} — PYG Neto{" "}
                  {totalNet.toLocaleString("es-CO")}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={`¿Estás seguro de eliminar el registro ${modalDetails.itemName}?`}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
        onConfirm={handleConfirmDelete}
      />

      {/* Modal genérico (placeholder) */}
      <CModal
        size="xl"
        alignment="center"
        visible={false}
        onClose={() => {}}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>Modal</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          {/* placeholder content */}
        </CModalBody>
      </CModal>
    </div>
  );
}
